import React from 'react';
import { useTranslation } from 'react-i18next';
import { AttendanceCountChip } from '@/pages/Overview/AttendanceCountChip';
import type { DisciplineTypeStatistics } from '@/pages/Overview/hooks/useAttendanceStatistics';
import { JustifiedReasons } from '@/pages/Overview/JustifiedReasons';
import { disciplineTypeLabelKeys } from '@/types/overview';
import { getPercent } from '@/utils/text';

interface Props {
  statistics: DisciplineTypeStatistics;
}

export const DisciplineTypeCard: React.FC<Props> = ({ statistics }) => {
  const { t } = useTranslation();
  const {
    type,
    attendableLessonsCount,
    total,
    justifiedCount,
    sicknessCount,
    otherCount,
    nonJustifiedCount,
  } = statistics;
  const attended = attendableLessonsCount - total;

  return (
    <div className="shadow-card flex flex-col gap-3 rounded-xl bg-popover p-4">
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <p className="text-subhead-14 md:text-callout-16 min-w-0 flex-1 truncate text-foreground">
            {t(disciplineTypeLabelKeys[type])}
          </p>
          <span className="flex shrink-0 items-center gap-2 whitespace-nowrap">
            <span className="text-body-14 text-subtle-foreground">
              {t('overview.attendance.absencesLowercase')}
            </span>
            <span className="text-subhead-14 text-foreground">{total}</span>
          </span>
        </div>
        <p className="text-subhead-14 text-primary">
          {t('overview.attendance.attendedCount', {
            attended,
            total: attendableLessonsCount,
          })}
        </p>
      </div>

      <div className="bg-on-primary-surface h-2 w-full overflow-hidden rounded-sm">
        <div
          className="h-full min-w-2 rounded-sm bg-primary"
          style={{ width: `${getPercent(attended, attendableLessonsCount)}%` }}
        />
      </div>

      <div className="flex flex-col gap-2">
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <p className="text-body-14 min-w-0 flex-1 text-foreground">
              {t('overview.attendance.justified')}
            </p>
            <AttendanceCountChip isJustified count={justifiedCount} />
          </div>
          <JustifiedReasons
            sicknessCount={sicknessCount}
            otherCount={otherCount}
          />
        </div>
        <div className="flex items-center gap-2">
          <p className="text-body-14 min-w-0 flex-1 text-foreground">
            {t('overview.attendance.nonJustified')}
          </p>
          <AttendanceCountChip isJustified={false} count={nonJustifiedCount} />
        </div>
      </div>
    </div>
  );
};
